import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import DailyScheduleService from "./DailyScheduleService";
import TimePicker from "./TimePicker";
import {
  minutesToTimeInput,
  timeInputToMinutes,
  normalizeActivity,
  todayDate,
} from "./data";

/**
 * Inline (non-modal) counterpart to EntryModal for the AI-transcript
 * flow. Same two-field form (Time + Activity), same validation rules
 * (dup on time OR activity, 120-char cap), but rendered as a card in
 * the transcript modal's right column — no backdrop / portal.
 *
 * Standalone DailySchedule keeps using EntryModal; nothing here touches
 * that path. This form's shape mirrors the other v2 section transcript
 * wrappers (initialPayload / onSave / saving / error / themeClass /
 * lockName).
 *
 * `existingList` is used for the frontend duplicate-name gate — parent
 * passes the timeline it renders, so both persisted rows and other
 * staged captured items are visible to the check. When the prop is
 * missing (defensive), a JIT fetch fills it so the check still bites
 * in review mode.
 */

const ACTIVITY_MAX_LEN = 500;  // mirrors EntryModal / the service cap

const ScheduleEntryForm = ({
  formId,
  patientId,
  initialPayload,
  existingList,
  saving,
  error,
  onSave,
  onReject,
  onClose,
  themeClass,
  lockName,
}) => {
  const p = initialPayload || {};

  // Seed values — AI-captured payload wins; otherwise blank.
  const seedTimeMinutes = (() => {
    if (typeof p.timeMinutes === "number") return p.timeMinutes;
    if (typeof p.time_minutes === "number") return p.time_minutes;
    return null;
  })();
  const initialTime = seedTimeMinutes != null ? minutesToTimeInput(seedTimeMinutes) : "";
  const initialActivity = p.activity || p.value || "";

  const [time, setTime] = useState(initialTime);
  const [activity, setActivity] = useState(initialActivity);
  const [feedback, setFeedback] = useState(null);
  const [fetchedList, setFetchedList] = useState(null);

  // If the parent didn't hand us `existingList`, fetch a fresh one
  // ourselves so the duplicate-name check has data. Prop wins otherwise.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (existingList != null) return;
    if (!patientId) return;
    // Transcript captures land on TODAY's day (the fan-out sends no
    // date), so the dedup check reads today's timeline.
    const svc = new DailyScheduleService();
    svc.list(
      patientId,
      todayDate(),
      (doc) => {
        if (!mounted.current) return;
        setFetchedList((doc && Array.isArray(doc.entries)) ? doc.entries : []);
      },
      () => {
        if (!mounted.current) return;
        setFetchedList([]);
      }
    );
  }, [patientId, existingList]);

  const effectiveExistingList = existingList != null ? existingList : (fetchedList || []);

  const timeMinutes = timeInputToMinutes(time);
  const activityTrimmed = normalizeActivity(activity);
  const activityKey = activityTrimmed.toLowerCase();

  const dupError = useMemo(() => {
    if (timeMinutes == null || !activityTrimmed) return "";
    for (const e of effectiveExistingList || []) {
      if (e.deleted) continue;
      // Skip our own staged row so the reviewer isn't self-blocked.
      if (e._captured && p && e._captured.id && p === (e._captured.current || null)) continue;
      if (e.timeMinutes === timeMinutes) {
        return "Another entry already exists at this time.";
      }
      if (normalizeActivity(e.activity).toLowerCase() === activityKey) {
        return `"${activityTrimmed}" is already on the schedule.`;
      }
    }
    return "";
  }, [timeMinutes, activityKey, activityTrimmed, effectiveExistingList, p]);

  const tooLong = activityTrimmed.length > ACTIVITY_MAX_LEN;
  const busy = !!saving;

  const canSubmit =
    !busy &&
    timeMinutes != null &&
    !!activityTrimmed &&
    !tooLong &&
    !dupError;

  const submit = () => {
    if (!canSubmit) return;
    setFeedback(null);
    if (typeof onSave === "function") {
      onSave({
        timeMinutes,
        activity: activityTrimmed,
      });
    }
  };

  const handleClose = () => onClose(formId);

  const rootClass = ["cc3-form", "ds3-form", themeClass || ""].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err" ? feedback.text : (error || "");
  const hasAlert = !!dupError || tooLong || !!displayError;

  return (
    <div className={rootClass}>
      <div className="cc3-lbl-row">
        <label className="cc3-lbl">
          Timeline entry
          {lockName && activityTrimmed && (
            <span className="cc3-update-name">&middot; {activityTrimmed}</span>
          )}
        </label>
      </div>

      {/* Vertical layout mirroring the standalone EntryModal — Time field
          above Activity field, both full-width. The previous build used
          the shared `.cc3-two` grid to force Time + Activity side-by-side
          in the right rail, but the Activity textarea needs enough width
          to hold a sentence and the Time input needs enough to show
          "07:00 am" + its clock button; the two squeeze each other and
          the card renders visibly cramped (ZY-10387 followup to ZY-10372).
          Stacking vertically costs ~60px of extra height and matches
          what Orbit's standalone Add-entry modal already shows. */}
      <div className="cc3-fold-inner">
        <div className="cc3-field">
          <label className="cc3-lbl" htmlFor={`sef-time-${formId}`}>
            Time<span className="cc3-req">*</span>
          </label>
          <TimePicker
            id={`sef-time-${formId}`}
            value={time}
            onChange={setTime}
            disabled={busy || lockName}
            inputClassName="sec-input"
          />
        </div>
        <div className="cc3-field">
          <div className="cc3-lbl-row">
            <label className="cc3-lbl" htmlFor={`sef-activity-${formId}`}>
              Activity<span className="cc3-req">*</span>
            </label>
            <span className="cc3-lbl-max">
              {activityTrimmed.length} / {ACTIVITY_MAX_LEN}
            </span>
          </div>
          <textarea
            id={`sef-activity-${formId}`}
            className={`sec-input ds4-textarea${tooLong ? " is-error" : ""}`}
            rows={3}
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
            placeholder="e.g. Evening walk"
            disabled={busy || lockName}
            maxLength={ACTIVITY_MAX_LEN + 20}
            aria-invalid={tooLong}
          />
        </div>

        {hasAlert && (
          <>
            {dupError && <div className="cc-feedback is-err">{dupError}</div>}
            {tooLong && (
              <div className="cc-feedback is-err">
                Activity is longer than {ACTIVITY_MAX_LEN} characters.
              </div>
            )}
            {displayError && !dupError && !tooLong && (
              <div className="cc-feedback is-err">{displayError}</div>
            )}
          </>
        )}

        <div className="cc3-foot">
          <button
            type="button"
            className="cc3-cancel"
            onClick={() => {
              if (!busy && typeof onReject === "function") {
                onReject();
                return;
              }
              handleClose();
            }}
          >
            {busy ? "Close" : (typeof onReject === "function" ? "Reject" : "Cancel")}
          </button>
          <button
            type="button"
            className={`cc3-save${themeClass ? ` ${themeClass}` : ""}`}
            onClick={submit}
            disabled={!canSubmit}
          >
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

ScheduleEntryForm.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  initialPayload: PropTypes.object,
  /** Current timeline the parent renders — feeds the duplicate-name check. */
  existingList: PropTypes.array,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onReject: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  themeClass: PropTypes.string,
  /** Freeze time + activity (transcript click-to-edit re-open). */
  lockName: PropTypes.bool,
};

ScheduleEntryForm.defaultProps = {
  patientId: undefined,
  initialPayload: null,
  existingList: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
};

export default ScheduleEntryForm;
